import { PeriodType } from '@thinkalpha/language-services';
import type { BarLength } from '@thinkalpha/ui-types/contracts/timeframe';
import type { DatafeedConfiguration, LibrarySessionId, ResolutionString } from 'charting_library';
import {
    defaultBarCloseFormula,
    defaultBarHighFormula,
    defaultBarLowFormula,
    defaultBarOpenFormula,
    defaultBarVolumeFormula,
} from 'src/lib/chart/defaultFormulas.js';

export const RESOLUTION_MAP: Record<ResolutionString, { platform: BarLength; bardex: string }> = {
    ['10S' as ResolutionString]: { platform: { value: 10, period: PeriodType.second }, bardex: '10s' },
    ['30S' as ResolutionString]: { platform: { value: 30, period: PeriodType.second }, bardex: '30s' },
    ['1' as ResolutionString]: { platform: { value: 1, period: PeriodType.minute }, bardex: '1m' },
    ['5' as ResolutionString]: { platform: { value: 5, period: PeriodType.minute }, bardex: '5m' },
    ['10' as ResolutionString]: { platform: { value: 10, period: PeriodType.minute }, bardex: '10m' },
    ['30' as ResolutionString]: { platform: { value: 30, period: PeriodType.minute }, bardex: '30m' },
    // ['60' as ResolutionString]: { platform: { value: 1, period: PeriodType.hour }, bardex: '1h' },
    ['1D' as ResolutionString]: { platform: { value: 1, period: PeriodType.day }, bardex: '1d' },
    ['1W' as ResolutionString]: { platform: { value: 1, period: PeriodType.week }, bardex: '1w' },
    ['1M' as ResolutionString]: { platform: { value: 1, period: PeriodType.month }, bardex: '1M' },
    // ['1Q' as ResolutionString]: { platform: { value: 1, period: PeriodType.quarter }, bardex: '1q' },
    ['1Y' as ResolutionString]: { platform: { value: 1, period: PeriodType.year }, bardex: '1y' },
};

export const DATAFEED_CONFIG: DatafeedConfiguration = {
    supported_resolutions: Object.keys(RESOLUTION_MAP),
    supports_timescale_marks: false,
    exchanges: [
        { value: '', name: 'All Exchanges', desc: 'All Exchanges' },
        { value: 'NYSE', name: 'NYSE', desc: 'New York Stock Exchange' },
        { value: 'NASDAQ', name: 'NASDAQ', desc: 'NASDAQ' },
    ],
    symbols_types: [
        { name: 'All types', value: '' },
        { name: 'Stock', value: 'stock' },
        { name: 'ETF', value: 'etf' },
    ],
};

export const BAR_SERIES = [
    { name: 'open', ...defaultBarOpenFormula },
    { name: 'high', ...defaultBarHighFormula },
    { name: 'low', ...defaultBarLowFormula },
    { name: 'close', ...defaultBarCloseFormula },
    { name: 'volume', ...defaultBarVolumeFormula },
];
export const HISTORIC_DATA_BATCH_SIZE = 5_000_000;

export const QUANT_NG_API_SESSION_RENAMES: Record<string, string> = {
    ['coreTrading']: 'Regular Trading Hours',
    ['preMarketTrading']: 'Pre-Market',
    ['postMarketTrading']: 'Post-Market',
    ['extended']: 'Extended Trading Hours',
    // Also map the TradingView session IDs for fallback lookup
    ['regular']: 'Regular Trading Hours',
    ['premarket']: 'Pre-Market',
    ['postmarket']: 'Post-Market',
};

/**
 * Maps market group names from the API to TradingView's expected session IDs.
 * TradingView uses specific session IDs like 'premarket' and 'postmarket' for
 * background color styling via the backgrounds.preMarket.color and
 * backgrounds.postMarket.color overrides.
 */
export const QUANT_NG_API_MARKET_GROUP_TO_TV_SESSION_ID: Record<string, LibrarySessionId> = {
    // Core/regular trading session names
    ['coreTrading']: 'regular',
    ['regular']: 'regular',
    ['Primary Trading Session']: 'regular',
    ['Core Trading Hours']: 'regular',
    ['Core Trading Session']: 'regular',

    // Pre-market trading session names
    ['preMarketTrading']: 'premarket',
    ['premarket']: 'premarket',
    ['preMarket']: 'premarket',
    ['pre']: 'premarket',
    ['earlyTrading']: 'premarket',
    ['Pre-Trading Session']: 'premarket',
    ['Pre-Market Hours']: 'premarket',

    // Post-market trading session names
    ['postMarketTrading']: 'postmarket',
    ['postmarket']: 'postmarket',
    ['postMarket']: 'postmarket',
    ['post']: 'postmarket',
    ['afterHours']: 'postmarket',
    ['lateTrading']: 'postmarket',
    ['Post-Trading Session']: 'postmarket',
    ['Post-Market Hours']: 'postmarket',
};
